/**
 * HardCopyTrackingPanel - Primary lifecycle tracker component.
 * Shows stepper timeline, tracking number, state details, and action buttons.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Printer, Send, PenTool, Upload, XCircle, Check,
  Clock, ChevronDown, ChevronUp, History,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrackingNumberBadge } from './TrackingNumberBadge';
import { HardCopyTrackingDialog, type DialogAction } from './HardCopyTrackingDialog';
import { TrackingAuditHistory } from './TrackingAuditHistory';
import { PrintableTrackingSheet } from './PrintableTrackingSheet';
import hardCopyTrackingAPI from '@/services/api/hardCopyTracking';
import {
  LIFECYCLE_STEPS,
  TRACKING_STATUS_CONFIG,
  type HardCopyTrackingRecord,
  type HardCopyTrackingStatus,
} from '@/types/hardCopyTracking';

interface HardCopyTrackingPanelProps {
  trackingId: number;
  onUpdate?: (record: HardCopyTrackingRecord) => void;
}

const STEP_ICONS = [FileText, Printer, Send, PenTool, Upload];

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString();
};

const formatDateTime = (dateStr: string | null) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

/**
 * Determines the next available action based on current status.
 */
function getNextAction(status: HardCopyTrackingStatus): DialogAction | null {
  switch (status) {
    case 'generated': return 'print';
    case 'printed': return 'provide';
    case 'provided_for_signature': return 'record-signature';
    case 'signed': return 'upload-signed-copy';
    default: return null;
  }
}

export function HardCopyTrackingPanel({ trackingId, onUpdate }: HardCopyTrackingPanelProps) {
  const [record, setRecord] = useState<HardCopyTrackingRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<DialogAction>('print');
  const [showHistory, setShowHistory] = useState(false);
  const [showPrintSheet, setShowPrintSheet] = useState(false);

  const loadRecord = useCallback(async () => {
    try {
      setLoading(true);
      const data = await hardCopyTrackingAPI.get(trackingId);
      setRecord(data);
    } catch {
      // handle error
    } finally {
      setLoading(false);
    }
  }, [trackingId]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  const openAction = (action: DialogAction) => {
    setDialogAction(action);
    setDialogOpen(true);
  };

  const handleComplete = (updated: HardCopyTrackingRecord) => {
    setRecord(updated);
    onUpdate?.(updated);
  };

  if (loading || !record) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </CardContent>
      </Card>
    );
  }

  const statusConfig = TRACKING_STATUS_CONFIG[record.status];
  const currentStep = statusConfig.step;
  const nextAction = getNextAction(record.status);
  const isCancelled = record.status === 'cancelled';
  const isComplete = record.status === 'uploaded_signed_copy';

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-base">
              <FileText className="w-5 h-5" />
              Hard-Copy Tracking
            </CardTitle>
            <TrackingNumberBadge
              trackingNumber={record.tracking_number}
              status={record.status}
              size="lg"
            />
          </div>

          {/* Target info */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
            {record.target_name && <span>{record.target_name}</span>}
            {record.program_name && (
              <Badge variant="outline" size="sm">{record.program_name}</Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Cancelled banner */}
          {isCancelled && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-md">
              <XCircle className="w-5 h-5 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">Cancelled</p>
                {record.cancellation_reason && (
                  <p className="text-xs mt-0.5">{record.cancellation_reason}</p>
                )}
              </div>
            </div>
          )}

          {/* Stepper */}
          <div className="flex items-center gap-0">
            {LIFECYCLE_STEPS.map((step, idx) => {
              const StepIcon = STEP_ICONS[idx];
              const isCompleted = !isCancelled && currentStep > idx;
              const isActive = !isCancelled && currentStep === idx;
              const isFuture = isCancelled || currentStep < idx;

              return (
                <React.Fragment key={step.key}>
                  {/* Step circle */}
                  <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                    <div
                      className={`
                        w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors
                        ${isCompleted ? 'bg-green-100 border-green-500 text-green-600' : ''}
                        ${isActive ? 'bg-primary/10 border-primary text-primary' : ''}
                        ${isFuture ? 'bg-gray-50 border-gray-200 text-gray-400' : ''}
                      `}
                    >
                      {isCompleted ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <StepIcon className="w-4 h-4" />
                      )}
                    </div>
                    <span className={`text-xs text-center leading-tight w-16
                      ${isActive ? 'font-medium text-primary' : 'text-muted-foreground'}
                    `}>
                      {step.label}
                    </span>
                  </div>

                  {/* Connector line */}
                  {idx < LIFECYCLE_STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mb-6 ${
                        isCompleted ? 'bg-green-400' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Step details */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {record.generated_at && (
              <div>
                <span className="text-muted-foreground">Generated:</span>{' '}
                <span>{formatDateTime(record.generated_at)}</span>
                {record.generated_by_name && (
                  <span className="text-muted-foreground"> by {record.generated_by_name}</span>
                )}
              </div>
            )}
            {record.printed_at && (
              <div>
                <span className="text-muted-foreground">Printed:</span>{' '}
                <span>{formatDateTime(record.printed_at)}</span>
                {record.printed_by_name && (
                  <span className="text-muted-foreground"> by {record.printed_by_name}</span>
                )}
              </div>
            )}
            {record.provided_at && (
              <div>
                <span className="text-muted-foreground">Provided:</span>{' '}
                <span>{formatDateTime(record.provided_at)}</span>
                {record.provided_by_name && (
                  <span className="text-muted-foreground"> by {record.provided_by_name}</span>
                )}
              </div>
            )}
            {record.member_signed_at && (
              <div>
                <span className="text-muted-foreground">Signed:</span>{' '}
                <span>{formatDate(record.member_signed_at)}</span>
                {record.signed_recorded_by_name && (
                  <span className="text-muted-foreground"> (recorded by {record.signed_recorded_by_name})</span>
                )}
              </div>
            )}
            {record.signed_copy_uploaded_at && (
              <div>
                <span className="text-muted-foreground">Scan uploaded:</span>{' '}
                <span>{formatDateTime(record.signed_copy_uploaded_at)}</span>
              </div>
            )}
            {record.provided_notes && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Notes:</span>{' '}
                <span>{record.provided_notes}</span>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {nextAction && (
              <Button onClick={() => openAction(nextAction)}>
                {nextAction === 'print' && <Printer className="w-4 h-4 mr-2" />}
                {nextAction === 'provide' && <Send className="w-4 h-4 mr-2" />}
                {nextAction === 'record-signature' && <PenTool className="w-4 h-4 mr-2" />}
                {nextAction === 'upload-signed-copy' && <Upload className="w-4 h-4 mr-2" />}
                {ACTION_LABELS[nextAction]}
              </Button>
            )}

            {!isCancelled && !isComplete && (
              <Button variant="outline" onClick={() => openAction('cancel')}>
                <XCircle className="w-4 h-4 mr-2" /> Cancel
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPrintSheet(true)}
            >
              <Printer className="w-4 h-4 mr-2" /> Print Sheet
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
            >
              <History className="w-4 h-4 mr-2" />
              {showHistory ? 'Hide' : 'Show'} History
              {showHistory ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
            </Button>
          </div>

          {/* Audit history (collapsible) */}
          {showHistory && (
            <div className="border-t pt-4">
              <TrackingAuditHistory trackingId={record.id} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transition dialog */}
      <HardCopyTrackingDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        action={dialogAction}
        trackingRecord={record}
        onComplete={handleComplete}
      />

      {/* Print sheet dialog */}
      {showPrintSheet && (
        <PrintableTrackingSheet
          record={record}
          onClose={() => setShowPrintSheet(false)}
        />
      )}
    </>
  );
}

const ACTION_LABELS: Record<string, string> = {
  print: 'Record Print',
  provide: 'Record Provided',
  'record-signature': 'Record Signature',
  'upload-signed-copy': 'Upload Signed Copy',
};
